import React, { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { cn } from '../../lib/utils';
import { Calendar as CalendarIcon } from '../../assets/Calendar';
import { SypacInput } from '@sypac/component-library-react';
import 'dayjs/locale/en';
import 'dayjs/locale/pl';
import 'dayjs/locale/ro';
import 'dayjs/locale/ru';
import { AnimatePresence, motion } from 'framer-motion';
import {
	useFloating,
	autoUpdate,
	offset,
	flip,
	shift,
	useInteractions,
	useRole,
	FloatingPortal,
	useId,
} from '@floating-ui/react';
import { CalendarRange } from './CalendarRange';
import { formatDate } from '../../utils/time.util';

export type DateRangeValue = {
	from: Date | null;
	to: Date | null;
};

export interface DateRangePickerProps {
	className?: string;
	label?: string;
	value: DateRangeValue;
	onChange: (next: DateRangeValue) => void;
	min?: string;
	max?: string;
	allowOtherMonthSelection?: boolean;
	locale?: string;
	error?: boolean;
	container?: HTMLElement | null;
	calendarPosition?: 'left' | 'right';
}

export function DateRangePicker({
	className,
	label = 'Filter by date',
	value,
	onChange,
	min,
	max,
	allowOtherMonthSelection = true,
	locale = 'en',
	error,
	container,
	calendarPosition = 'right',
}: DateRangePickerProps) {
	dayjs.locale(locale);

	const [isOpen, setIsOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const calendarRef = useRef<HTMLDivElement>(null);

	const [validContainer, setValidContainer] = useState<HTMLElement | null>(
		null,
	);

	useEffect(() => {
		if (container) {
			setValidContainer(container);
			return;
		}

		if (containerRef.current) {
			const modalOrSidebarRoot = containerRef.current.closest(
				'.modal-inside,.modal-overlay,[data-modal-root],[data-sidebar-root],[role="dialog"]',
			) as HTMLElement | null;

			setValidContainer(modalOrSidebarRoot || document.body);
		} else {
			setValidContainer(document.body);
		}
	}, [container]);

	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		placement: 'bottom',
		strategy: 'fixed',
		middleware: [
			offset({ mainAxis: 8 }),
			flip({ fallbackPlacements: ['top'] }),
			shift({ padding: 8 }),
		],
		whileElementsMounted: autoUpdate,
	});

	const role = useRole(context);
	const { getReferenceProps, getFloatingProps } = useInteractions([role]);

	const headingId = useId();
	const floatingProps = getFloatingProps();

	const summary = useMemo(() => {
		if (value.from && value.to) {
			return `${formatDate(value.from, 'DD MMM YYYY')} – ${formatDate(
				value.to,
				'DD MMM YYYY',
			)}`;
		}
		if (value.from) return formatDate(value.from, 'DD MMM YYYY');
		return '';
	}, [value.from, value.to]);

	const handleCalendarInteraction = (e: React.MouseEvent) => {
		e.stopPropagation();
		e.nativeEvent?.stopImmediatePropagation?.();
	};

	useEffect(() => {
		if (!isOpen) return;

		const handleClickOutside = (e: MouseEvent) => {
			const target = e.target as Node;

			const clickedOutside =
				!containerRef.current?.contains(target) &&
				!calendarRef.current?.contains(target);

			const isInModal = !!(target as HTMLElement).closest(
				'[data-modal-root], .modal, #datepicker-modal',
			);

			if (clickedOutside && !isInModal) setIsOpen(false);
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen]);

	const handlePick = (picked: Date) => {
		const clicked = dayjs(picked).startOf('day');
		const start = value.from ? dayjs(value.from).startOf('day') : null;
		const end = value.to ? dayjs(value.to).startOf('day') : null;

		if (!start || (start && end)) {
			onChange({ from: clicked.toDate(), to: null });
			return;
		}

		if (start.isSame(clicked, 'day')) {
			onChange({ from: null, to: null });
			setIsOpen(false);
			return;
		}

		const from = clicked.isBefore(start) ? clicked : start;
		const to = clicked.isBefore(start) ? start : clicked;

		onChange({ from: from.toDate(), to: to.toDate() });
		setIsOpen(false);
	};

	return (
		<div ref={containerRef} className={cn('relative', className)}>
			<div ref={refs.setReference} {...getReferenceProps()}>
				<SypacInput className="relative">
					<button
						type="button"
						onClick={() => setIsOpen((p) => !p)}
						className={cn(
							'w-full text-left py-2.5 border border-solid rounded-10 bg-white cursor-pointer',
							error
								? 'border-red-orange'
								: 'border-gray-10 hover:border-gray-40',
							calendarPosition === 'right' ? 'pl-3 pr-10' : 'pl-[35px] pr-3',
						)}
						aria-expanded={isOpen}
						aria-haspopup="dialog"
						aria-controls={isOpen ? headingId : undefined}
					>
						{!value.from && !value.to ? (
							<span className="text-gray-40 text-[14px] leading-[20px]">
								{label}
							</span>
						) : null}

						{summary ? (
							<span
								className={cn(
									'text-gray-80 text-[14px] leading-[20px]',
									!value.from && !value.to ? 'ml-2' : '',
								)}
							>
								{!(!value.from && !value.to) ? summary : `• ${summary}`}
							</span>
						) : null}
					</button>

					<span
						className={cn(
							'absolute',
							calendarPosition === 'right'
								? 'top-3 right-4'
								: 'top-[11px] left-3',
						)}
					>
						<CalendarIcon />
					</span>
				</SypacInput>
			</div>

			{isOpen && validContainer && (
				<FloatingPortal root={validContainer}>
					<div
						ref={(node) => {
							refs.setFloating(node);
							// @ts-ignore
							calendarRef.current = node;
						}}
						style={{ ...floatingStyles, zIndex: 9999 }}
						{...floatingProps}
						onMouseDown={(e) => {
							handleCalendarInteraction(e);
							// @ts-ignore
							floatingProps.onMouseDown?.(e);
						}}
						onClick={(e) => {
							handleCalendarInteraction(e);
							// @ts-ignore
							floatingProps.onClick?.(e);
						}}
					>
						<AnimatePresence>
							<motion.div
								initial={{ opacity: 0, y: -10 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -10 }}
								transition={{ duration: 0.2 }}
								className="shadow-lg rounded-lg bg-white"
								id={headingId}
							>
								<CalendarRange
									initialDate={value.to ?? value.from ?? undefined}
									rangeStart={value.from ?? undefined}
									rangeEnd={value.to ?? undefined}
									onDateClick={handlePick}
									minDate={min ? new Date(min) : undefined}
									maxDate={max ? new Date(max) : undefined}
									allowOtherMonthSelection={allowOtherMonthSelection}
									locale={locale}
								/>
							</motion.div>
						</AnimatePresence>
					</div>
				</FloatingPortal>
			)}
		</div>
	);
}
